import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, LogOut } from 'lucide-react'
import { toast } from 'sonner'
import { useCreateWorkspace } from '@/hooks/use-workspace-admin'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import { workspaceSchema, type WorkspaceInput } from '@/lib/validations'
import { slugify } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/ui/field'
import { AuthShell } from '@/features/auth/auth-shell'

export function CreateWorkspaceScreen() {
  const createWorkspace = useCreateWorkspace()
  const { refetch, setCurrentWorkspace } = useWorkspace()
  const { signOut, profile } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<WorkspaceInput>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: { name: '', slug: '' },
  })

  const name = watch('name')
  useEffect(() => {
    if (name) setValue('slug', slugify(name), { shouldValidate: true })
  }, [name, setValue])

  async function onSubmit(values: WorkspaceInput) {
    setSubmitting(true)
    try {
      const id = await createWorkspace.mutateAsync({ ...values, description: values.description })
      await refetch()
      setCurrentWorkspace(id)
      toast.success('Workspace criado')
    } catch (err) {
      toast.error('Não foi possível criar', {
        description: err instanceof Error ? err.message : undefined,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell
      title="Criar seu workspace"
      subtitle={`Olá, ${profile?.full_name ?? 'bem-vindo'}. Comece criando um espaço de trabalho.`}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Field label="Nome" htmlFor="name" required error={errors.name?.message}>
          <Input id="name" placeholder="TEXA do Brasil" {...register('name')} />
        </Field>
        <Field
          label="Identificador (slug)"
          htmlFor="slug"
          required
          error={errors.slug?.message}
          hint="Usado internamente. Apenas letras minúsculas, números e hífen."
        >
          <Input id="slug" placeholder="texa-brasil" {...register('slug')} />
        </Field>
        <Field label="Descrição" htmlFor="description" error={errors.description?.message}>
          <Textarea
            id="description"
            placeholder="Marketing — CAR, BIKE, TRUCK, OHW"
            {...register('description')}
          />
        </Field>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Criar workspace
        </Button>
      </form>
      <Button
        variant="ghost"
        size="sm"
        className="mt-4 w-full text-muted-foreground"
        onClick={() => signOut()}
      >
        <LogOut className="h-4 w-4" /> Sair
      </Button>
    </AuthShell>
  )
}
